import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Section, ColorInput, ImageUpload, TextField, TextAreaField } from "@/components/branding/BrandingForm";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Save, RotateCcw } from "lucide-react";

const EMPTY = {
  primary_color: "", secondary_color: "", accent_color: "",
  logo_url: "", image_url: "", tagline: "",
  season: "", conference: "", mascot: "",
  head_coach: "", home_venue: "",
  twitter_url: "", instagram_url: "",
  description: "",
};

export default function SportBrandingTab({ sport, canManage, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [initial, setInitial] = useState(EMPTY);
  const [siteDefaults, setSiteDefaults] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [siteRes] = await Promise.all([api.get("/settings")]);
    setSiteDefaults(siteRes.data);
    const merged = { ...EMPTY };
    Object.keys(EMPTY).forEach(k => { merged[k] = sport?.[k] || ""; });
    setForm(merged); setInitial(merged);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sport?.id]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const dirty = JSON.stringify(form) !== JSON.stringify(initial);

  const save = async () => {
    if (!canManage) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/sports/${sport.id}`, form);
      const merged = { ...EMPTY };
      Object.keys(EMPTY).forEach(k => { merged[k] = data?.[k] || ""; });
      setForm(merged); setInitial(merged);
      toast.success("Sport branding saved");
      onSaved && onSaved();
      window.dispatchEvent(new Event("settings-updated"));
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const inherit = (siteKey) => siteDefaults?.[siteKey] ? `Inherits: ${siteDefaults[siteKey]}` : "Overrides site default when set.";

  return (
    <div>
      <p className="text-sm text-slate-600 mb-3">
        Sport-specific branding overrides the site defaults for {sport.name}. All fields are optional.
      </p>
      <div className="flex justify-end gap-2 mb-3">
        <Button variant="outline" className="rounded-sm" disabled={!dirty || !canManage} onClick={() => setForm(initial)} data-testid="sport-brand-reset">
          <RotateCcw className="w-4 h-4 mr-1"/>Reset
        </Button>
        <Button className="rounded-sm bg-[#0A1128] hover:bg-slate-800 font-heading" disabled={!dirty || saving || !canManage} onClick={save} data-testid="sport-brand-save">
          <Save className="w-4 h-4 mr-1"/>{saving ? "Saving..." : "Save"}
        </Button>
      </div>

      <div className="space-y-4">
        <Section title="Team Colors" subtitle="Applied to this sport's page. Falls back to site colors when blank.">
          <ColorInput label={`Primary — ${inherit("primary_color")}`} value={form.primary_color} onChange={v => set("primary_color", v)} testid="sport-primary-color"/>
          <ColorInput label={`Secondary — ${inherit("secondary_color")}`} value={form.secondary_color} onChange={v => set("secondary_color", v)} testid="sport-secondary-color"/>
          <ColorInput label={`Accent — ${inherit("accent_color")}`} value={form.accent_color} onChange={v => set("accent_color", v)} testid="sport-accent-color"/>
        </Section>

        <Section title="Sport Identity">
          <TextField label="Tagline" value={form.tagline} onChange={v => set("tagline", v)} placeholder="Gridiron Glory since 1963" testid="sport-tagline"/>
          <TextField label="Season display" value={form.season} onChange={v => set("season", v)} placeholder="2025-2026 Season" helpText={inherit("season")} testid="sport-season"/>
          <TextField label="Conference" value={form.conference} onChange={v => set("conference", v)} placeholder="Region 4 — Class 3A" helpText={inherit("conference")} testid="sport-conference"/>
          <TextField label="Mascot (if different)" value={form.mascot} onChange={v => set("mascot", v)} placeholder="Tigers" helpText={inherit("mascot")} testid="sport-mascot"/>
          <TextField label="Head coach" value={form.head_coach} onChange={v => set("head_coach", v)} placeholder="Coach Johnson" testid="sport-head-coach"/>
          <TextField label="Home venue" value={form.home_venue} onChange={v => set("home_venue", v)} placeholder="Tiger Field" testid="sport-home-venue"/>
          <div className="md:col-span-2">
            <TextAreaField label="Description" value={form.description} onChange={v => set("description", v)} placeholder="Overview of the program..." testid="sport-description-branding"/>
          </div>
        </Section>

        <Section title="Imagery">
          <ImageUpload label="Sport logo (overrides site logo on this sport)" value={form.logo_url} onChange={v => set("logo_url", v)} hint="Small icon shown on sport page." testid="sport-logo"/>
          <ImageUpload label="Cover image (sport page hero)" value={form.image_url} onChange={v => set("image_url", v)} hint="Large image used on the sport's public page." testid="sport-cover" maxKb={800}/>
        </Section>

        <Section title="Sport Social">
          <TextField label="Twitter / X" value={form.twitter_url} onChange={v => set("twitter_url", v)} placeholder="https://x.com/tigersfootball" testid="sport-twitter"/>
          <TextField label="Instagram" value={form.instagram_url} onChange={v => set("instagram_url", v)} placeholder="https://instagram.com/tigersfootball" testid="sport-instagram"/>
        </Section>
      </div>
    </div>
  );
}
